<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import {
    checkHubspotAuthenticationStatus,
    fetchJsonWithHubspotAuth,
    HUBSPOT_AUTH_CHANGED_EVENT,
    openHubspotAuthDialog,
    restoreHubspotSession,
  } from "../lib/hubspotAuth";

  const API_URL = `${process.env.SVELTE_APP_FE_REMOTE_URL}/api/alace/field-configurations`;
  const SETTINGS_URL = `${process.env.SVELTE_APP_FE_REMOTE_URL}/api/hubspot-config/settings`;
  const HUBSPOT_PROPERTIES_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/hubspot-sync/properties/deals`;
  const DEFAULT_HUBSPOT_FIELDS_URL = `${process.env.SVELTE_APP_ALADV_BASE_URL}/aladv/hubspot-deal-properties-v2s`;

  type HubspotProperty = {
    name: string;
    label: string;
    type?: string;
    calculated?: boolean | number;
    formField?: boolean | number;
  };

  type FieldMapping = {
    key: string;
    label: string;
    defaultHubspotField: string;
    defaultAcelinkField: string;
    required?: boolean;
    id?: number;
    customField: string;
  };

  type FieldGroup = {
    title: string;
    description?: string;
    fields: FieldMapping[];
  };

  const standardHubspotFields: FieldMapping[] = [
    {
      key: "projectTitleName",
      label: "Project Title",
      defaultHubspotField: "dealname",
      defaultAcelinkField: "partnerProjectTitle",
      required: true,
      customField: "",
    },
    {
      key: "targetCloseDateName",
      label: "Close Date",
      defaultHubspotField: "closedate",
      defaultAcelinkField: "targetCloseDate",
      required: true,
      customField: "",
    },
  ];

  const requiredHubspotFields = new Set([
    "customer_company_name_v2",
    "industry_vertical_v2",
    "country_v2",
    "opportunity_type_v2",
    "customer_business_problem_v2",
    "use_case_v2",
    "delivery_model_v2",
    "sales_activities_v2",
    "partner_primary_needs_from_aws_v2",
    "does_opp_belong_to_nat_sec_v2",
    "customer_contacts_v2",
    "opportunity_team_v2",
  ]);

  const explicitFieldKeys: Record<string, string> = {
    aws_opportunity_id_v2: "awsOppIdName",
    partner_crm_unique_id_v2: "partnerCrmUniqueIdName",
    parent_opportunity_id_v2: "parentOppIdName",
    sync_to_aws_v2: "syncToAwsName",
    last_synced_to_aws_v2: "lastSyncedToAwsName",
    target_close_date_v2: "targetCloseDateName",
    partner_project_title_v2: "projectTitleName",
    estimated_aws_monthly_recurring_revenue_v2: "estimatedAwsMonthlyReccuringRevenueName",
    partner_primary_needs_from_aws_v2: "partnerPrimaryNeedFromAwsName",
    does_opp_belong_to_nat_sec_v2: "doesOppBelongToNatSecName",
    acelink_sync_status_v2: "acelinkSyncStatusName",
    acelink_sync_message_v2: "acelinkSyncMessageName",
    acelink_validation_status_v2: "acelinkValidationStatusName",
  };

  const explicitAcelinkFields: Record<string, string> = {
    aws_opportunity_id_v2: "awsOppId",
    partner_crm_unique_id_v2: "partnerCrmUniqueId",
    parent_opportunity_id_v2: "parentOppId",
    sync_to_aws_v2: "syncToAws",
    last_synced_to_aws_v2: "lastSyncedToAws",
    target_close_date_v2: "targetCloseDate",
    partner_project_title_v2: "partnerProjectTitle",
    estimated_aws_monthly_recurring_revenue_v2: "estimatedAwsMonthlyReccuringRevenue",
    partner_primary_needs_from_aws_v2: "partnerPrimaryNeedFromAws",
    does_opp_belong_to_nat_sec_v2: "doesOppBelongToNatSec",
  };

  let fieldGroups: FieldGroup[] = [];
  let fieldMappings: FieldMapping[] = [];
  $: fieldMappings = fieldGroups.flatMap((group) => group.fields);

  let config: Record<string, any> = {};
  let configId: number | null = null;
  let isLoading = false;
  let propertiesLoading = false;
  let saveMessage = "";
  let saveMessageType: "success" | "error" = "success";
  let propertiesMessage = "";
  let hubspotProperties: HubspotProperty[] = [];
  let hubspotAuthChecked = false;
  let isHubspotAuthenticated = false;
  let authMessage = "";
  let expandedGroups: Record<string, boolean> = {};
  $: allGroupsExpanded = fieldGroups.length > 0 && fieldGroups.every((group) => expandedGroups[group.title]);

  const toCamelCase = (value: string) =>
    value.replace(/_([a-z0-9])/g, (_, letter: string) => letter.toUpperCase());

  const toFieldKey = (hubspotField: string) => {
    if (explicitFieldKeys[hubspotField]) return explicitFieldKeys[hubspotField];
    const withoutSuffix = hubspotField.replace(/_v2$/, "");
    return `${toCamelCase(withoutSuffix)}Name`;
  };

  const toAcelinkField = (hubspotField: string) => {
    if (explicitAcelinkFields[hubspotField]) return explicitAcelinkFields[hubspotField];
    return toCamelCase(hubspotField.replace(/_v2$/, ""));
  };

  const categorizeField = (hubspotField: string) => {
    if (
      [
        "aws_opportunity_id_v2",
        "aws_opportunity_id_custom_v2",
        "partner_crm_unique_id_v2",
        "parent_opportunity_id_v2",
        "sync_to_aws_v2",
        "last_synced_to_aws_v2",
        "closed_v2",
      ].includes(hubspotField)
    ) {
      return "Core Identifiers";
    }
    if (
      [
        "customer_company_name_v2",
        "customer_duns_v2",
        "industry_vertical_v2",
        "industry_other_v2",
        "website_v2",
        "aws_account_id_v2",
        "customer_contacts_v2",
      ].includes(hubspotField)
    ) {
      return "Customer";
    }
    if (
      ["street_address_v2", "city_v2", "state_v2", "postal_code_v2", "country_v2"].includes(hubspotField)
    ) {
      return "Address";
    }
    if (
      [
        "marketing_campaign_v2",
        "aws_funding_used_v2",
        "marketing_source_v2",
        "marketing_activity_channel_v2",
        "marketing_activity_use_case_v2",
      ].includes(hubspotField)
    ) {
      return "Marketing";
    }
    if (
      [
        "aws_stage_v2",
        "review_status_v2",
        "closed_lost_reason_v2",
        "next_steps_v2",
        "next_steps_history_v2",
        "recommendation_v2",
        "aws_mandatory_field_context_v2",
        "aws_mandatory_field_fill_log_v2",
      ].includes(hubspotField)
    ) {
      return "Lifecycle";
    }
    if (
      ["acelink_sync_status_v2", "acelink_sync_message_v2", "acelink_validation_status_v2"].includes(hubspotField)
    ) {
      return "Sync Feedback";
    }
    return "Project";
  };

  const buildFieldGroups = (defaultProperties: HubspotProperty[]) => {
    const mappings = defaultProperties
      .filter((property) => property.name && property.name.endsWith("_v2"))
      .filter((property) => property.name !== "aws_opportunity_id_custom_v2")
      .map((property) => ({
        key: toFieldKey(property.name),
        label: property.label || property.name,
        defaultHubspotField: property.name,
        defaultAcelinkField: toAcelinkField(property.name),
        required: requiredHubspotFields.has(property.name),
        customField: "",
      }));

    const byKey = new Map<string, FieldMapping>();
    [...standardHubspotFields, ...mappings].forEach((field) => {
      if (!byKey.has(field.key)) byKey.set(field.key, { ...field });
    });

    const allMappings = Array.from(byKey.values());
    const requiredFields = allMappings.filter((field) => field.required);
    const optionalFields = allMappings.filter((field) => !field.required);
    const groupOrder = ["Core Identifiers", "Customer", "Address", "Project", "Marketing", "Lifecycle", "Sync Feedback"];
    fieldGroups = [
      {
        title: "Required AWS Fields",
        description: "Fields that commonly cannot be empty when syncing to AWS.",
        fields: requiredFields,
      },
      ...groupOrder
        .map((title) => ({
          title,
          fields: optionalFields.filter((field) => categorizeField(field.defaultHubspotField) === title),
        }))
        .filter((group) => group.fields.length > 0),
    ];
    expandedGroups = Object.fromEntries(fieldGroups.map((group) => [group.title, false]));
  };

  const toggleGroup = (title: string) => {
    expandedGroups = {
      ...expandedGroups,
      [title]: !expandedGroups[title],
    };
  };

  const expandAllGroups = () => {
    expandedGroups = Object.fromEntries(fieldGroups.map((group) => [group.title, true]));
  };

  const collapseAllGroups = () => {
    expandedGroups = Object.fromEntries(fieldGroups.map((group) => [group.title, false]));
  };

  const fetchDefaultHubspotFields = async () => {
    const res = await fetch(DEFAULT_HUBSPOT_FIELDS_URL);
    if (!res.ok) throw new Error("Failed to fetch default HubSpot fields");
    const data = await res.json();
    buildFieldGroups(Array.isArray(data) ? data : []);
  };

  const fetchConfiguration = async () => {
    const [settingsRes, fieldConfigRes] = await Promise.all([
      fetch(SETTINGS_URL),
      fetch(API_URL),
    ]);
    const res = settingsRes;
    if (!res.ok) throw new Error("Failed to fetch field configuration");
    const data = await res.json();
    const fieldConfigs = fieldConfigRes.ok ? await fieldConfigRes.json() : [];
    const fieldConfigByKey = new Map(
      (Array.isArray(fieldConfigs) ? fieldConfigs : []).map((item: any) => [
        item.fieldKey,
        item,
      ])
    );
    configId = data?.id ?? null;
    config = { ...data };
    fieldMappings.forEach((field) => {
      const savedField = fieldConfigByKey.get(field.key);
      field.id = savedField?.id;
      field.customField = savedField?.customHubspotField || data?.[field.key] || "";
    });
  };

  const fetchHubspotProperties = async () => {
    propertiesLoading = true;
    propertiesMessage = "";
    try {
      const { response, data, text } = await fetchJsonWithHubspotAuth(
        HUBSPOT_PROPERTIES_URL,
        undefined,
        {
          authRequiredMessage: "Sign in to HubSpot to load selectable HubSpot fields.",
          openDialogOnAuthError: false,
        }
      );
      const result = data as any;
      if (!response.ok || result?.success === false) {
        throw new Error(result?.error || text || "Failed to fetch HubSpot properties");
      }
      isHubspotAuthenticated = true;
      hubspotProperties = (result?.properties || [])
        .map((property: HubspotProperty) => ({
          name: property.name,
          label: property.label || property.name,
          type: property.type,
        }))
        .sort((a: HubspotProperty, b: HubspotProperty) => a.label.localeCompare(b.label));
    } catch (err) {
      console.error("Error loading HubSpot properties:", err);
      propertiesMessage = err instanceof Error ? err.message : "Failed to fetch HubSpot properties.";
      hubspotProperties = [];
      if (propertiesMessage) {
        isHubspotAuthenticated = false;
        hubspotAuthChecked = true;
      }
    } finally {
      propertiesLoading = false;
    }
  };

  const refreshHubspotAuthStatus = async () => {
    hubspotAuthChecked = false;
    authMessage = "";
    try {
      isHubspotAuthenticated = await checkHubspotAuthenticationStatus();
      if (!isHubspotAuthenticated) {
        const restored = await restoreHubspotSession();
        isHubspotAuthenticated = restored
          ? await checkHubspotAuthenticationStatus().catch(() => true)
          : false;
      }
    } catch {
      isHubspotAuthenticated = false;
    } finally {
      hubspotAuthChecked = true;
    }
  };

  const connectHubspot = () => {
    authMessage = "";
    openHubspotAuthDialog("Sign in to HubSpot to load selectable HubSpot fields.");
  };

  const saveConfiguration = async () => {
    isLoading = true;
    saveMessage = "";
    try {
      await Promise.all(fieldMappings.map(async (field) => {
        const customHubspotField = (field.customField || "").trim();
        if (field.id) {
          const res = customHubspotField
            ? await fetch(`${API_URL}/${field.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ customHubspotField }),
              })
            : await fetch(`${API_URL}/${field.id}`, { method: "DELETE" });
          if (!res.ok) throw new Error("Failed to save field configuration");
          if (!customHubspotField) field.id = undefined;
          return;
        }

        if (!customHubspotField) return;
        const res = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fieldKey: field.key, customHubspotField }),
        });
        if (!res.ok) throw new Error("Failed to save field configuration");
        const data = await res.json();
        field.id = data?.id;
      }));

      config = {
        ...config,
        ...Object.fromEntries(fieldMappings.map((field) => [field.key, field.customField || ""])),
      };
      saveMessage = "Field configuration saved";
      saveMessageType = "success";
    } catch (err) {
      saveMessage = err instanceof Error ? err.message : "Failed to save field configuration";
      saveMessageType = "error";
    } finally {
      isLoading = false;
    }
  };

  onMount(async () => {
    try {
      await fetchDefaultHubspotFields();
      await fetchConfiguration();
    } catch (err) {
      console.error("Error loading field configuration:", err);
      saveMessage = "Failed to load field configuration";
      saveMessageType = "error";
    }
    await refreshHubspotAuthStatus();
    await fetchHubspotProperties();
    window.addEventListener(HUBSPOT_AUTH_CHANGED_EVENT, refreshHubspotAuthStatus);
  });

  onDestroy(() => {
    window.removeEventListener(HUBSPOT_AUTH_CHANGED_EVENT, refreshHubspotAuthStatus);
  });
</script>

<div class="fields-shell mx-auto mt-10 p-6 rounded-xl w-full">
  {#if hubspotAuthChecked && !isHubspotAuthenticated}
    <div class="mb-6 rounded-lg border border-orange-500/40 bg-orange-500/10 p-4">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p class="text-sm font-semibold text-orange-200">HubSpot authentication required</p>
          <p class="mt-1 text-sm text-orange-300">
            Sign in to HubSpot to load the selectable HubSpot fields.
          </p>
        </div>
        <button
          type="button"
          class="hubspot-connect-button"
          on:click={connectHubspot}
        >
          Connect HubSpot
        </button>
      </div>
      {#if authMessage}
        <p class="mt-2 text-sm text-orange-300">{authMessage}</p>
      {/if}
    </div>
  {/if}

  <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-6">
    <div>
      <h2 class="text-2xl font-bold text-secondary-200">Field Configuration</h2>
      <p class="mt-1 text-sm text-secondary-400">
        Configure default and alternative HubSpot field mappings.
      </p>
    </div>
    <button
      type="button"
      class="px-6 py-2 bg-secondary-800 border border-secondary-600 text-secondary-200 rounded-lg hover:bg-secondary-700 hover:text-white disabled:opacity-50"
      on:click={fetchHubspotProperties}
      disabled={propertiesLoading}
    >
      {propertiesLoading ? "Refreshing..." : "Refresh HubSpot Fields"}
    </button>
  </div>

  <form on:submit|preventDefault={saveConfiguration} class="space-y-6">
    <div class="flex justify-end">
      <button
        type="button"
        class="px-6 py-2 bg-secondary-800 border border-secondary-600 text-secondary-200 rounded-lg hover:bg-secondary-700 hover:text-white disabled:opacity-50"
        on:click={allGroupsExpanded ? collapseAllGroups : expandAllGroups}
      >
        {allGroupsExpanded ? "Collapse all" : "Expand all"}
      </button>
    </div>

    {#each fieldGroups as group}
      <section class="border border-secondary-700 rounded-xl overflow-hidden">
        <button
          type="button"
          class="flex w-full items-center justify-between gap-4 bg-secondary-800 px-4 py-4 text-left hover:bg-secondary-700"
          on:click={() => toggleGroup(group.title)}
          aria-expanded={expandedGroups[group.title] ? "true" : "false"}
        >
          <div>
            <h3 class="text-lg font-semibold text-secondary-200">{group.title}</h3>
            {#if group.description}
              <p class="mt-1 text-sm text-secondary-400">{group.description}</p>
            {/if}
          </div>
          <div class="flex items-center gap-3">
            <span class="rounded bg-secondary-700 px-2 py-1 text-xs font-medium text-secondary-400">
              {group.fields.length} fields
            </span>
            <span class="text-xl leading-none text-secondary-400">
              {expandedGroups[group.title] ? "−" : "+"}
            </span>
          </div>
        </button>

        {#if expandedGroups[group.title]}
          <div class="space-y-4 border-t border-secondary-700 p-4">
            {#each group.fields as field}
              <div class="rounded border border-secondary-800 bg-secondary-900/50 p-4">
                <div class="mb-3 flex items-center gap-2">
                  <h4 class="text-base font-semibold text-secondary-200">{field.label}</h4>
                  {#if field.required}
                    <span class="rounded bg-red-500/15 px-2 py-0.5 text-xs font-medium text-red-300">AWS required</span>
                  {/if}
                </div>
                <div class="grid gap-4 md:grid-cols-3">
                  <div>
                    <label class="block text-sm font-medium text-secondary-300 mb-1" for={`${field.key}-hubspot`}>
                      Default HubSpot Field
                    </label>
                    <input
                      id={`${field.key}-hubspot`}
                      type="text"
                      class="w-full border rounded px-3 py-2 bg-secondary-800 text-secondary-400"
                      value={field.defaultHubspotField}
                      readonly
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-secondary-300 mb-1" for={`${field.key}-acelink`}>
                      Default Acelink Field
                    </label>
                    <input
                      id={`${field.key}-acelink`}
                      type="text"
                      class="w-full border rounded px-3 py-2 bg-secondary-800 text-secondary-400"
                      value={field.defaultAcelinkField}
                      readonly
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-secondary-300 mb-1" for={field.key}>
                      Alternative HubSpot Field
                    </label>
                    <select
                      id={field.key}
                      bind:value={field.customField}
                      class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      disabled={propertiesLoading}
                    >
                      <option value="">Use default HubSpot field</option>
                      {#each hubspotProperties as property}
                        <option value={property.name}>
                          {property.label} ({property.name})
                        </option>
                      {/each}
                    </select>
                    <p class="mt-1 text-xs text-secondary-400">
                      Leave empty to use the generated default field.
                    </p>
                  </div>
                </div>
              </div>
            {/each}
          </div>
        {/if}
      </section>
    {/each}

    {#if propertiesMessage}
      <div class="p-3 rounded bg-amber-500/10 border border-amber-500/40 text-amber-200 text-sm">
        {propertiesMessage}
      </div>
    {/if}

    {#if saveMessage}
      <div
        class={`p-3 rounded text-sm ${
          saveMessageType === "success"
            ? "bg-green-100 text-green-700"
            : "bg-red-500/10 border border-red-500/40 text-red-200"
        }`}
      >
        {saveMessage}
      </div>
    {/if}

    <div class="flex justify-end">
      <button
        type="submit"
        class="px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-500 disabled:opacity-50"
        disabled={isLoading}
      >
        {isLoading ? "Saving..." : "Save Field Configuration"}
      </button>
    </div>
  </form>
</div>

<style>
  .fields-shell {
    background: #24283a;
    border: 1px solid rgba(226, 232, 240, 0.14);
  }

  .fields-shell :global(.bg-secondary-800) {
    background-color: #262a3d !important;
  }
  .fields-shell :global(.bg-secondary-900\/50),
  .fields-shell :global(.bg-secondary-900) {
    background-color: #1e2233 !important;
  }
  .fields-shell :global(.bg-secondary-700),
  .fields-shell :global(.hover\:bg-secondary-700:hover) {
    background-color: #343a52 !important;
  }
  .fields-shell :global(.border-secondary-800) {
    border-color: #2c3145 !important;
  }
  .fields-shell :global(.border-secondary-700) {
    border-color: #383e55 !important;
  }
  .fields-shell :global(.border-secondary-600) {
    border-color: #444a63 !important;
  }

  .fields-shell :global(input),
  .fields-shell :global(select),
  .fields-shell :global(textarea) {
    background-color: #1a1e2f;
    color: #f1f5f9;
    border-color: #383e55;
    color-scheme: dark;
  }

  .fields-shell :global(input::placeholder) {
    color: #64748b;
  }

  .hubspot-connect-button {
    appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 140px;
    border: 1px solid rgba(251, 146, 60, 0.85);
    border-radius: 0.75rem;
    background: linear-gradient(180deg, #ff9437 0%, #ff7a1a 100%);
    color: #ffffff;
    padding: 0.65rem 1rem;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.2;
    box-shadow: 0 10px 18px rgba(249, 115, 22, 0.22);
    transition:
      background-color 150ms ease,
      border-color 150ms ease,
      box-shadow 150ms ease;
  }

  .hubspot-connect-button:hover {
    background: linear-gradient(180deg, #ff973e 0%, #ff7f20 100%);
    border-color: rgba(251, 146, 60, 1);
    box-shadow: 0 12px 22px rgba(249, 115, 22, 0.28);
  }
</style>
